import type { DefenseType } from "./hero.types"

export type UpgradeColumn = "military" | "arcane" | "command"
export type UpgradeBranch = "a" | "b" | null // null = root upgrade
export type UpgradeType = "passive" | "active"

export type UpgradeTarget =
  | "all-towers"
  | "archer"
  | "barracks"
  | "soldiers"
  | "mage"
  | "siege"
  | "hero"
  | "shop"
  | "map"

export type UpgradeStat =
  | "damage"
  | "range"
  | "attackSpeed"
  | "spawnCount"
  | "hp"
  | "speed"
  | "cooldown"
  | "price"
  | "revealNodes"
  | "defenseType" // For soldier armor type upgrades

export interface UpgradeEffect {
  target: UpgradeTarget
  stat?: UpgradeStat
  value?: number | string // Multiplier (1.1 = +10%) or flat value, or string for defenseType
  isFlat?: boolean // true = flat value, false = multiplier
  ability?: string // For active abilities
  isFirstPurchaseOnly?: boolean // Only apply this effect on first purchase (e.g., armor type)
}

export interface UpgradeDefinition {
  id: string
  name: string
  description: string
  column: UpgradeColumn
  branch: UpgradeBranch
  tier: number // 0 = root, 1-3 = branch upgrades
  cost: number
  type: UpgradeType
  icon?: string // Placeholder for now
  effects: UpgradeEffect[]
  maxPurchases: number // 1 for tier-3 (capstones), 5 for others
}

export interface UpgradeState {
  purchaseCounts: Record<string, number> // upgradeId -> number of times purchased
  selectedBranches: {
    military: UpgradeBranch
    arcane: UpgradeBranch
    command: UpgradeBranch
  }
}

export type UpgradeNodeStatus = "locked" | "available" | "unlocked" | "blocked" | "maxed"

export interface UpgradeModifiers {
  // Tower modifiers
  allTowerDamage: number
  archerDamage: number
  archerRange: number
  archerSpeed: number
  barracksSpawnCount: number
  soldierHp: number
  mageDamage: number
  siegeDamage: number
  // Hero modifiers
  heroSpeed: number
  heroCooldown: number
  heroHp: number
  // Economy/Map modifiers
  shopDiscount: number
  mapRevealNodes: number
  // Soldier modifiers (for upgrades)
  soldierDefenseType: DefenseType // Default "light", Reinforcements gives "medium", Hardened Veterans gives "heavy"
  soldierDamageMultiplier: number // Battle Cry damage multiplier (default 1.0)
  soldierArmorMultiplier: number // Battle Cry armor multiplier (default 1.0)
}

export const initialUpgradeState: UpgradeState = {
  purchaseCounts: {},
  selectedBranches: {
    military: null,
    arcane: null,
    command: null,
  },
}

export const initialUpgradeModifiers: UpgradeModifiers = {
  allTowerDamage: 1.0,
  archerDamage: 1.0,
  archerRange: 1.0,
  archerSpeed: 1.0,
  barracksSpawnCount: 0,
  soldierHp: 1.0,
  mageDamage: 1.0,
  siegeDamage: 1.0,
  heroSpeed: 1.0,
  heroCooldown: 1.0,
  heroHp: 1.0,
  shopDiscount: 0,
  mapRevealNodes: 0,
  soldierDefenseType: "light",
  soldierDamageMultiplier: 1.0,
  soldierArmorMultiplier: 1.0,
}
